import { useCallback, useRef, useState } from "react";
import type { FrozenExpectation } from "../booking/attempt";
import { normalizeCode } from "./answers";
import { beginCheckout, freezeSubmission, isSameIdentity, verifiedEmailFrom, type CheckoutAttempt } from "./checkout";
import {
  DEFAULT_POLICY,
  INITIAL,
  canRequestCode,
  isExactReplayConflict,
  canVerify,
  isVerifyPaused,
  reduce,
  type Event,
  type GuestCheckoutState,
  type Policy,
} from "./machine";
import type { GuestCheckoutPort } from "./port";
import { reportReplayConflict } from "../../../../../shared/errorReporting";
import type { TermsDecision } from "../terms/terms";
import type { BookingCancellationPolicyDisplay } from "../profile/operations";
import type { PolicyCheck } from "../profile/policy";

export interface GuestCheckoutDeps {
  port: GuestCheckoutPort;
  /** Returns the CURRENT anonymous session. Never switches identity on its own account. */
  ensureIdentity: () => Promise<{ uid: string }>;
  webHost: string;
  now?: () => number;
  /** A monotonic clock (rc.11 1c): `performance.now` unless a test supplies one. */
  monoNow?: () => number;
  isOnline?: () => boolean;
  policy?: Policy;
  /**
   * False when a newer release is live (features/release/staleBuild.ts): no
   * code request and no booking is sent from an old build (rc.9 item 2). The
   * Check of an uncertain booking never asks: a reload there would lose it.
   */
  currentBuild?: () => Promise<boolean>;
  /** rc.11 1c: the privacy-safe report of a conflict to an exact replay (tests supply a spy). */
  reportReplayConflict?: () => void;
}

/**
 * A port that throws is treated as one that lost its answer. For `submit` that
 * is the conservative reading — the booking MAY exist — and it keeps the
 * frozen submission as the only thing that may be sent next.
 */
async function orLost<T>(call: () => Promise<T>, lost: T): Promise<T> {
  try {
    return await call();
  } catch {
    return lost;
  }
}

const performanceNow = () => performance.now();

/** What `verifyAndBook` needs to freeze a submission. */
export interface SubmitContext {
  holdId: string | null;
  fullName: string;
  expected: FrozenExpectation;
  /** The hold's expiry as the server sent it (rc.11 1c); absent: a replay is never definite. */
  holdExpiresAt?: string | null;
}

/**
 * The hook the screens talk to.
 *
 * DOUBLE TAPS. Every dispatching action takes one synchronous latch (`inFlight`)
 * before it awaits anything. React state is not a latch: two taps in one task
 * both read the pre-update value. The latch is released in `finally`, always.
 *
 * IDENTITY. Every dispatch first re-reads the anonymous session. If its uid is
 * not the uid the checkout's key is bound to, nothing is sent: before
 * submission the checkout ends (`identity-lost`), after submission the check
 * is withdrawn and the page says so. A new key is never minted to "resolve"
 * an outcome — that is how a second booking gets made.
 *
 * OFFLINE. Dispatches are refused while offline. Nothing is queued to go later.
 */
export function useGuestCheckout(deps: GuestCheckoutDeps) {
  const now = deps.now ?? Date.now;
  const monoNow = deps.monoNow ?? performanceNow;
  const isOnline = deps.isOnline ?? (() => (typeof navigator === "undefined" ? true : navigator.onLine));
  const policy = deps.policy ?? DEFAULT_POLICY;

  const [state, setState] = useState<GuestCheckoutState>(INITIAL);
  const stateRef = useRef<GuestCheckoutState>(INITIAL);
  const inFlight = useRef(false);

  const dispatch = useCallback((event: Event) => {
    const next = reduce(stateRef.current, event);
    stateRef.current = next;
    setState(next);
    return next;
  }, []);

  /** The attempt this dispatch may use, or null (and the state says why). */
  const identityFor = useCallback(async (): Promise<CheckoutAttempt | null> => {
    let uid: string;
    try {
      uid = (await deps.ensureIdentity()).uid;
      if (!uid) throw new Error("no uid");
    } catch {
      dispatch({ type: "START_FAILED" });
      return null;
    }
    const current = stateRef.current.attempt;
    if (current) {
      if (!isSameIdentity(current, uid)) {
        dispatch({ type: "IDENTITY_LOST" });
        return null;
      }
      return current;
    }
    try {
      return beginCheckout(uid);
    } catch {
      dispatch({ type: "START_FAILED" });
      return null;
    }
  }, [deps, dispatch]);

  /**
   * `terms`: the decision made on Review. REQUIRED for the first request (the
   * code is never sent before the guest has decided); a resend or a changed
   * address omits it and keeps the one held.
   */
  const requestCode = useCallback(
    async (typedEmail: string, terms?: TermsDecision, policy?: BookingCancellationPolicyDisplay | null) => {
      if (inFlight.current || !isOnline()) return;
      if (!canRequestCode(stateRef.current, now())) return;
      if (!terms && !stateRef.current.terms) return;
      const email = verifiedEmailFrom(typedEmail);
      if (!email) return;
      inFlight.current = true;
      try {
        if (deps.currentBuild && !(await deps.currentBuild())) return;
        const attempt = await identityFor();
        if (!attempt) return;
        dispatch({ type: "REQUEST_STARTED", attempt, email, ...(terms ? { terms, policy: policy ?? null } : {}) });
        if (stateRef.current.phase.name !== "requesting") return;
        const call = await orLost(
          () => deps.port.requestCode({ email, idempotencyKey: attempt.idempotencyKey }),
          { kind: "lost" } as const
        );
        dispatch({ type: "REQUEST_FINISHED", call, now: now() });
      } finally {
        inFlight.current = false;
      }
    },
    [deps.port, deps.currentBuild, dispatch, identityFor, isOnline, now]
  );

  /** Same address, same key. Enabled only by `resendAfter` or a limiter's retry time. */
  const resend = useCallback(async () => {
    const email = stateRef.current.email;
    if (email) await requestCode(email);
  }, [requestCode]);

  const submit = useCallback(
    async (context: SubmitContext) => {
      const s = stateRef.current;
      if (s.phase.name !== "ready" || !s.proof || !s.attempt || !s.email || s.holdExpired || !s.terms) return;
      if (!context.holdId) return;
      const submission = freezeSubmission(s.attempt, {
        holdId: context.holdId,
        fullName: context.fullName,
        verifiedEmail: s.email,
        verificationId: s.proof.verificationId,
        expected: context.expected,
        terms: s.terms,
        // When it goes, immediately below (rc.11 1c).
        sent: { mono: monoNow(), at: now(), holdExpiresAt: context.holdExpiresAt ? Date.parse(context.holdExpiresAt) : Number.NaN },
      });
      dispatch({ type: "SUBMIT_STARTED", submission });
      if (stateRef.current.phase.name !== "submitting") return;
      const result = await orLost(() => deps.port.submit(submission.input), { kind: "lost" } as const);
      dispatch({ type: "SUBMIT_FINISHED", result, webHost: deps.webHost, now: now() });
    },
    [deps.port, deps.webHost, dispatch, monoNow, now]
  );

  /**
   * One tap: check the code, and on a verified proof book immediately, as the
   * design's single "Confirm booking" does. Also the action from `ready`
   * (verified earlier, booking not yet sent).
   */
  const verifyAndBook = useCallback(
    async (
      typedCode: string,
      context: SubmitContext,
      /**
       * rc.10: the policy re-read before anything is sent, against the policy
       * agreed on Review. "changed" returns to Review (nothing sent, the
       * challenge kept); "unreadable" stops here (the caller says why, and the
       * next press is the retry).
       */
      policyCheck?: () => Promise<PolicyCheck>
    ) => {
      if (inFlight.current || !isOnline()) return;
      inFlight.current = true;
      try {
        if (deps.currentBuild && !(await deps.currentBuild())) return;
        if (policyCheck) {
          const check = await policyCheck();
          if (check === "changed") dispatch({ type: "POLICY_CHANGED" });
          if (check !== "same") return;
        }
        const attempt = await identityFor();
        if (!attempt) return;
        if (stateRef.current.phase.name === "code") {
          const code = normalizeCode(typedCode);
          const challenge = stateRef.current.challenge;
          if (!code || !challenge || !canVerify(stateRef.current, now())) return;
          dispatch({ type: "VERIFY_STARTED" });
          const call = await orLost(
            () =>
              deps.port.verifyCode({
                verificationId: challenge.verificationId,
                idempotencyKey: attempt.idempotencyKey,
                oneTimeCode: code,
              }),
            { kind: "lost" } as const
          );
          dispatch({ type: "VERIFY_FINISHED", call, now: now() });
        }
        if (stateRef.current.phase.name !== "ready") return;
        // THE SEND AFTER THE VERIFY IS ITS OWN SEND. The session and the
        // connection were checked before the verify, and either can change
        // while it is in flight; the booking must not ride on that earlier
        // check. (QE A2-1: a uid swapped mid-verify used to book under the old
        // uid's key, and going offline mid-verify still sent the booking.)
        if (!isOnline()) return;
        if (!(await identityFor())) return;
        await submit(context);
      } finally {
        inFlight.current = false;
      }
    },
    [deps.port, deps.currentBuild, dispatch, identityFor, isOnline, now, submit]
  );

  /** Ask about THE SAME submission. Never a new key, never rebuilt input. */
  const check = useCallback(async () => {
    if (inFlight.current || !isOnline()) return;
    const s = stateRef.current;
    if (s.phase.name !== "uncertain" || !s.phase.canCheck || !s.submission) return;
    inFlight.current = true;
    try {
      const attempt = await identityFor();
      if (!attempt) return;
      // The replay's send instant and identity decide what a refusal means (rc.11 1c).
      dispatch({ type: "CHECK_STARTED", now: now(), mono: monoNow(), uid: attempt.uid });
      const submission = stateRef.current.submission;
      if (!submission || stateRef.current.phase.name !== "uncertain") return;
      const result = await orLost(() => deps.port.submit(submission.input), { kind: "lost" } as const);
      // A conflict to this exact replay is a Backend defect: its code is reported, nothing else (1c).
      if (isExactReplayConflict(stateRef.current, result)) (deps.reportReplayConflict ?? reportReplayConflict)();
      dispatch({ type: "CHECK_FINISHED", result, webHost: deps.webHost });
    } finally {
      inFlight.current = false;
    }
  }, [deps.port, deps.webHost, dispatch, identityFor, isOnline, monoNow, now]);

  return {
    state,
    requestCode,
    resend,
    verifyAndBook,
    check,
    editEmail: useCallback(() => dispatch({ type: "EDIT_EMAIL", now: now() }), [dispatch, now]),
    cancelEdit: useCallback(() => dispatch({ type: "EDIT_CANCELLED" }), [dispatch]),
    holdExpired: useCallback(() => dispatch({ type: "HOLD_EXPIRED", now: now() }), [dispatch, now]),
    newHold: useCallback(() => dispatch({ type: "NEW_HOLD", now: now(), policy }), [dispatch, now, policy]),
    restart: useCallback(() => dispatch({ type: "RESTART", now: now() }), [dispatch, now]),
    /**
     * After a Terms refusal (the proof held) or a policy change found at
     * Verify (the challenge held, rc.10): the guest agreed again on Review. No
     * network; the code screen's Verify is the send.
     */
    acceptTerms: useCallback(
      (terms: TermsDecision, policy?: BookingCancellationPolicyDisplay | null) => dispatch({ type: "TERMS_ACCEPTED", terms, policy: policy ?? null }),
      [dispatch]
    ),
    /**
     * Apply a Details edit, or defer it through an attempt pause. Returns the
     * instant the pause lifts when deferred (the caller re-applies then), or
     * null when the edit was dispatched.
     */
    detailsChanged: useCallback(
      (email: string): number | null => {
        const at = now();
        const current = stateRef.current;
        if (isVerifyPaused(current, at) && current.phase.name === "code" && current.phase.note.kind === "verify-limited") {
          return current.phase.note.retryAt;
        }
        dispatch({ type: "DETAILS_CHANGED", email, now: at });
        return null;
      },
      [dispatch, now]
    ),
    contactRejectionSeen: useCallback(() => dispatch({ type: "CONTACT_REJECTION_SEEN" }), [dispatch]),
  };
}
